import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  MenuToggle,
  type MenuToggleElement,
  Pagination,
  SearchInput,
  Select,
  SelectList,
  SelectOption,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { ActionsColumn, type IAction, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import {
  ArchiveIcon,
  ExclamationCircleIcon,
  FileAltIcon,
  PlusCircleIcon,
  SearchIcon,
  SyncAltIcon,
} from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useNewsletterTemplates } from '../../../hooks/useNewsletterTemplates';
import type { NewsletterTemplate, NewsletterTemplateSummary } from '../../../types/models/NewsletterTemplate';
import { formatDateNumeric } from '../../../utils/dateFormat';
import { formatBytes } from '../../../utils/formatBytes';
import NewsletterTemplateModal from './NewsletterTemplateModal';
import NewsletterTemplateArchiveModal from './NewsletterTemplateArchiveModal';

/** Which server-side set the list shows: active templates or the archived (soft-deleted) set. */
type ArchivedFilter = 'active' | 'archived';

const ARCHIVED_FILTER_LABEL: Record<ArchivedFilter, string> = {
  active: 'Active',
  archived: 'Archived',
};

const isArchivedFilter = (v: unknown): v is ArchivedFilter => v === 'active' || v === 'archived';

/**
 * NewsletterTemplatesTab — staff CRUD over the Twig templates the newsletter send pipeline renders,
 * living between the Recipients and Newsletters tabs of the Ancalagon-staff-only Newsletter admin
 * page.
 *
 * The Active/Archived selector chooses the server-side set (`?archived=1`); search over the template
 * name and pagination are client-side over the fetched set (the list endpoint exposes no search or
 * pagination params). It fetches its own data, so the parent `<Tabs>` uses `mountOnEnter` and this
 * tab exposes a Refresh action for manual re-runs.
 */
function NewsletterTemplatesTab() {
  const [archivedFilter, setArchivedFilter] = useState<ArchivedFilter>('active');
  const isArchivedView = archivedFilter === 'archived';

  const { templates, loading, error, refetch, applyTemplate, removeTemplate } = useNewsletterTemplates(isArchivedView);

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<NewsletterTemplateSummary | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<NewsletterTemplateSummary | null>(null);

  // Debounce the search so filtering doesn't run on every keystroke; reset to page 1 on change.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  const filteredTemplates = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) {
      return templates;
    }
    return templates.filter((t) => t.name.toLowerCase().includes(q));
  }, [templates, debouncedSearch]);

  // Clamp the page when the filtered set shrinks below it (archive removes a row, a refresh returns
  // fewer, perPage grows), so the table never renders an empty page past the end.
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredTemplates.length / perPage));
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredTemplates.length, perPage, page]);

  const paginatedTemplates = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredTemplates.slice(start, start + perPage);
  }, [filteredTemplates, page, perPage]);

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, pageNumber: number) => {
    setPage(pageNumber);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchValue('');
    // Clear the debounced value directly so the table recovers immediately, not after the 400ms tick.
    setDebouncedSearch('');
    setPage(1);
  };

  const selectView = (next: ArchivedFilter) => {
    setArchivedFilter(next);
    setPage(1);
    setIsFilterOpen(false);
  };

  // A save from the modal: upsert the row, then — if the user CREATED a template while viewing the
  // Archived set — switch to Active so their new (always-active) template is actually visible.
  const handleSaved = (saved: NewsletterTemplate) => {
    const wasCreate = editTarget === null;
    applyTemplate(saved);
    if (wasCreate && isArchivedView) {
      selectView('active');
    }
  };

  const rowActions = (template: NewsletterTemplateSummary): IAction[] => {
    const actions: IAction[] = [{ title: 'Modify', onClick: () => setEditTarget(template) }];
    // Archived templates have nowhere further to go (no restore endpoint), so only offer Archive on
    // the active set.
    if (!template.is_archived) {
      actions.push(
        { isSeparator: true },
        { title: 'Archive', isDanger: true, onClick: () => setArchiveTarget(template) },
      );
    }
    return actions;
  };

  const renderBody = () => {
    if (error && templates.length === 0) {
      return (
        <EmptyState
          titleText="Unable to load templates"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" icon={<SyncAltIcon />} onClick={() => void refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (templates.length === 0) {
      // Current view is genuinely empty (no rows at all, not just filtered out).
      return isArchivedView ? (
        <EmptyState titleText="No archived templates" icon={ArchiveIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>Templates you archive will appear here. Nothing has been archived yet.</EmptyStateBody>
        </EmptyState>
      ) : (
        <EmptyState titleText="No templates yet" icon={FileAltIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            Create your first newsletter template. Author the body elsewhere (e.g. Claude Design) and paste the Twig
            markup in — it is syntax-checked when you save.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsCreateOpen(true)}>
                Create template
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (filteredTemplates.length === 0) {
      return (
        <EmptyState titleText="No templates match your search" icon={SearchIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>Try a different search term, or clear the search to see all templates.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={clearSearch}>
                Clear search
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <>
        <Table aria-label="Newsletter templates list" variant="compact">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Size</Th>
              <Th>Created</Th>
              <Th>{isArchivedView ? 'Archived' : 'Updated'}</Th>
              <Th screenReaderText="Row actions" />
            </Tr>
          </Thead>
          <Tbody>
            {paginatedTemplates.map((template) => (
              <Tr key={template.id}>
                <Td dataLabel="Name">{template.name}</Td>
                <Td dataLabel="Size">{formatBytes(template.twig_bytes)}</Td>
                <Td dataLabel="Created">{formatDateNumeric(template.created_at)}</Td>
                <Td dataLabel={isArchivedView ? 'Archived' : 'Updated'}>
                  {formatDateNumeric(isArchivedView ? template.archived_at : template.updated_at)}
                </Td>
                <Td isActionCell>
                  <ActionsColumn items={rowActions(template)} />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        <Pagination
          itemCount={filteredTemplates.length}
          perPage={perPage}
          page={page}
          onSetPage={onSetPage}
          onPerPageSelect={onPerPageSelect}
          variant="bottom"
        />
      </>
    );
  };

  // Full-panel spinner only when we have nothing to show: the initial load, and a view switch (which
  // clears the rows). A same-view refresh keeps its rows, so the table stays put.
  if (loading && templates.length === 0) {
    return <CenteredSpinner />;
  }

  return (
    <>
      <Toolbar>
        <ToolbarContent>
          <ToolbarItem>
            <SearchInput
              placeholder="Search by name..."
              value={searchValue}
              onChange={(_event, value) => setSearchValue(value)}
              onClear={() => setSearchValue('')}
            />
          </ToolbarItem>
          <ToolbarItem>
            <Select
              id="newsletter-template-archived-filter"
              isOpen={isFilterOpen}
              selected={archivedFilter}
              onSelect={(_event, value) => {
                if (isArchivedFilter(value)) {
                  selectView(value);
                }
              }}
              onOpenChange={(open) => setIsFilterOpen(open)}
              toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                <MenuToggle
                  ref={toggleRef}
                  onClick={() => setIsFilterOpen((open) => !open)}
                  isExpanded={isFilterOpen}
                  isDisabled={loading}
                  aria-label="Filter by archived status"
                >
                  {ARCHIVED_FILTER_LABEL[archivedFilter]}
                </MenuToggle>
              )}
              shouldFocusToggleOnSelect
            >
              <SelectList>
                <SelectOption value="active">Active</SelectOption>
                <SelectOption value="archived">Archived</SelectOption>
              </SelectList>
            </Select>
          </ToolbarItem>
          <ToolbarItem>
            <Button
              variant="secondary"
              icon={<SyncAltIcon />}
              onClick={() => void refetch()}
              isLoading={loading}
              isDisabled={loading}
            >
              Refresh
            </Button>
          </ToolbarItem>
          <ToolbarItem>
            <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsCreateOpen(true)}>
              Create template
            </Button>
          </ToolbarItem>
          <ToolbarItem variant="pagination">
            <Pagination
              itemCount={filteredTemplates.length}
              perPage={perPage}
              page={page}
              onSetPage={onSetPage}
              onPerPageSelect={onPerPageSelect}
            />
          </ToolbarItem>
        </ToolbarContent>
      </Toolbar>

      {/* A background refresh/view-switch failed but we still have rows to show — surface it inline
          rather than blowing the table away (that only happens when there's no data at all). */}
      {error && templates.length > 0 && (
        <Alert variant="danger" isInline title="Couldn't refresh templates" className="pf-v6-u-mb-md">
          {error}
        </Alert>
      )}

      {renderBody()}

      {(isCreateOpen || editTarget !== null) && (
        <NewsletterTemplateModal
          template={editTarget}
          onClose={() => {
            setIsCreateOpen(false);
            setEditTarget(null);
          }}
          onSaved={handleSaved}
        />
      )}
      {archiveTarget !== null && (
        <NewsletterTemplateArchiveModal
          template={archiveTarget}
          onClose={() => setArchiveTarget(null)}
          onArchived={removeTemplate}
        />
      )}
    </>
  );
}

export default NewsletterTemplatesTab;
